import * as React from "react";
import IllustrationCreateNewWorkflow1 from "@/icons/illustrations/IllustrationCreateNewWorkflow1";
import IllustrationBuildItWithAI from "@/icons/illustrations/IllustrationBuildItWithAI";
import IllustrationChat from "@/icons/illustrations/IllustrationChat";
import IllustrationChatbotLeadGen from "@/icons/illustrations/IllustrationChatbotLeadGen";
import IllustrationAfterHours from "@/icons/illustrations/IllustrationAfterHours";
import IllustrationFeedbackSurveyPostChat from "@/icons/illustrations/IllustrationFeedbackSurveyPostChat";
import IllustrationPreChatSurvey from "@/icons/illustrations/IllustrationPreChatSurvey";
import IllustrationChatbotFAQ from "@/icons/illustrations/IllustrationChatbotFAQ";
import IllustrationHelpCenterSearchArticlesKB from "@/icons/illustrations/IllustrationHelpCenterSearchArticlesKB";
import IllustrationChatPausedIdleDelay from "@/icons/illustrations/IllustrationChatPausedIdleDelay";
import IllustrationProactiveChatCommunication from "@/icons/illustrations/IllustrationProactiveChatCommunication";
import IllustrationMissedChatsUnavailable from "@/icons/illustrations/IllustrationMissedChatsUnavailable";
import IllustrationChatbotSupportService from "@/icons/illustrations/IllustrationChatbotSupportService";
import IllustrationChatTransfersReAssignments1 from "@/icons/illustrations/IllustrationChatTransfersReAssignments1";
import { useState, useEffect } from "react";
import TriggerModal from "./TriggerModal";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import useActivityLog from "@/hooks/useActivityLog";

// ── Integration template node definitions ────────────────────────────────────
// These pre-build the workflow structure. AI/NetSuite/Shopify nodes with null
// configIds are intentional — the user fills them in the editor.

const TEMPLATE_NODES = {
  "netsuite-ai-assistant": {
    trigger: "incoming_message",
    name: "NetSuite AI Assistant",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "incoming_message", overrideRouting: true, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Hi! I can look up your account, orders, and support cases. What can I help you with today?", child: 3 },
      "3": { id: 3, type: "action", actionType: "netsuiteAI", netSuiteConfigId: null, mcpServerIds: [] }
    }
  },
  "netsuite-lead-capture": {
    trigger: "incoming_message",
    name: "Lead Capture → NetSuite",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "incoming_message", overrideRouting: true, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Thanks for reaching out! I'd like to capture your contact details.", child: 3 },
      "3": { id: 3, type: "action", actionType: "askQuestion", question: "What's your full name?", questionType: "text", propName: "name", saveAsContactProp: true, saveAsConversationProp: false, saveAsWorkflowProp: true, invalidReplyAction: "repeat", child: 4 },
      "4": { id: 4, type: "action", actionType: "askQuestion", question: "What's your email address?", questionType: "email", propName: "email", saveAsContactProp: true, saveAsConversationProp: false, saveAsWorkflowProp: true, invalidReplyAction: "repeat", child: 5 },
      "5": { id: 5, type: "action", actionType: "askQuestion", question: "What company are you with?", questionType: "text", propName: "company", saveAsContactProp: true, saveAsConversationProp: false, saveAsWorkflowProp: true, invalidReplyAction: "repeat", child: 6 },
      "6": { id: 6, type: "action", actionType: "netsuiteAI", netSuiteConfigId: null, mcpServerIds: [] }
    }
  },
  "shopify-order-status": {
    trigger: "incoming_message",
    name: "Shopify Order Status Bot",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "incoming_message", overrideRouting: true, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Hi! I can check on your orders and help you find products. What do you need?", child: 3 },
      "3": { id: 3, type: "action", actionType: "transferToAI", aiConfigId: null, mcpServerIds: [] }
    }
  },
  "shopify-product-discovery": {
    trigger: "conversation_opened",
    name: "Shopify Product Discovery",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "conversation_opened", overrideRouting: false, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Welcome to our store! I can help you find the perfect product. What are you looking for?", child: 3 },
      "3": { id: 3, type: "action", actionType: "transferToAI", aiConfigId: null, mcpServerIds: [] }
    }
  },
  "shopify-cart-recovery": {
    trigger: "conversation_opened",
    name: "Shopify Cart Recovery Bot",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "conversation_opened", overrideRouting: true, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Hi! I noticed you left some items in your cart. I can help you complete your purchase or answer any questions — what's on your mind?", child: 3 },
      "3": { id: 3, type: "action", actionType: "transferToAI", aiConfigId: null, mcpServerIds: [] }
    }
  },
  "bigcommerce-order-status": {
    trigger: "conversation_opened",
    name: "BigCommerce Order Status Bot",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "conversation_opened", overrideRouting: true, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Hi! I can look up your orders and help with your account. What's your order number or email address?", child: 3 },
      "3": { id: 3, type: "action", actionType: "bigCommerceAI", bigCommerceConfigId: null, aiConfigId: null, mcpServerIds: [] }
    }
  },
  "bigcommerce-product-assistant": {
    trigger: "conversation_opened",
    name: "BigCommerce Product Assistant",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "conversation_opened", overrideRouting: true, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Hi! I can help you find the perfect product. What are you looking for?", child: 3 },
      "3": { id: 3, type: "action", actionType: "bigCommerceAI", bigCommerceConfigId: null, aiConfigId: null, mcpServerIds: [] }
    }
  },
  "bigcommerce-cart-recovery": {
    trigger: "conversation_opened",
    name: "BigCommerce Cart Recovery",
    nodes: {
      "1": { id: 1, type: "trigger", trigger: "conversation_opened", overrideRouting: true, channels: [], triggerExpression: null },
      "2": { id: 2, type: "action", actionType: "sendMessage", text: "Hi! I noticed you left some items in your cart. I can help you complete your purchase or answer any questions.", child: 3 },
      "3": { id: 3, type: "action", actionType: "bigCommerceAI", bigCommerceConfigId: null, aiConfigId: null, mcpServerIds: [] }
    }
  }
} as const;

type IntegrationTemplateKey = keyof typeof TEMPLATE_NODES;

const startBuildingCards = [
  {
    icon: <IllustrationCreateNewWorkflow1 />,
    title: "Create New",
    text: "Start with a blank canvas and build your custom workflow",
    template: "blank"
  },
  {
    icon: <IllustrationBuildItWithAI />,
    title: "Build it with AI",
    text: "Build workflows with AI-powered guidance, saving time and effort",
    template: "build-with-ai"
  }
];
const templateCards = [
  {
    icon: <IllustrationChat />,
    title: "Welcome Series",
    text: "Send a series of engaging messages to new website visitors",
    template: "welcome-series"
  },
  {
    icon: <IllustrationChatbotLeadGen />,
    title: "Lead Generation Chatbot",
    text: "Boosts leads with a chatbot that captures and engages effortlessly",
    template: "lead-gen-chatbot"
  },
  {
    icon: <IllustrationAfterHours />,
    title: "Out of Business Hours",
    text: "Automate replies for inquiries outside business hours",
    template: "after-hours"
  },
  {
    icon: <IllustrationFeedbackSurveyPostChat />,
    title: "Satisfaction Surveys",
    text: "Collect feedback after interactions to improve customer experience",
    template: "satisfaction-surveys"
  },
  {
    icon: <IllustrationPreChatSurvey />,
    title: "Pre-Chat Surveys",
    text: "Collect user information before the start of a conversation",
    template: "pre-chat-surveys"
  },
  {
    icon: <IllustrationChatbotFAQ />,
    title: "FAQ Chatbot",
    text: "Use an AI chatbot to handle frequently asked questions",
    template: "faq-chatbot"
  },
  {
    icon: <IllustrationChatTransfersReAssignments1 />,
    title: "Auto-Assignment & Handoff",
    text: "Auto-route incoming messages to the appropriate agents or teams",
    template: "auto-assignment-handoff"
  },
  {
    icon: <IllustrationHelpCenterSearchArticlesKB />,
    title: "Smart Article Guide",
    text: "Share relevant articles by detecting kewords in messages",
    template: "smart-article-guide"
  },
  {
    icon: <IllustrationChatPausedIdleDelay />,
    title: "Idle Contact",
    text: "Check in on unresponsive customers during conversations",
    template: "idle-contact"
  },
  {
    icon: <IllustrationProactiveChatCommunication />,
    title: "Proactive Engagement",
    text: "Identify high-intent visitors, engage, and drive conversations",
    template: "proactive-engagement"
  },
  {
    icon: <IllustrationMissedChatsUnavailable />,
    title: "Missed Conversations",
    text: "Reconnect with customers that are unable to connect with a live agent",
    template: "missed-conversations"
  },
  {
    icon: <IllustrationChatbotSupportService />,
    title: "Service Chatbot",
    text: "Create a service but to provide support for your visitors",
    template: "service-chatbot"
  }
];

const integrationTemplateCards = [
  {
    icon: <IllustrationChatbotSupportService />,
    title: "NetSuite AI Assistant",
    text: "Let AI handle account lookups, order status, and support cases via NetSuite",
    template: "netsuite-ai-assistant" as IntegrationTemplateKey,
    badge: "NetSuite"
  },
  {
    icon: <IllustrationChatbotLeadGen />,
    title: "Lead Capture → NetSuite",
    text: "Collect contact details and automatically create a lead in NetSuite",
    template: "netsuite-lead-capture" as IntegrationTemplateKey,
    badge: "NetSuite"
  },
  {
    icon: <IllustrationChat />,
    title: "Shopify Order Status Bot",
    text: "Let visitors check order status and get product help via your Shopify store",
    template: "shopify-order-status" as IntegrationTemplateKey,
    badge: "Shopify"
  },
  {
    icon: <IllustrationProactiveChatCommunication />,
    title: "Shopify Product Discovery",
    text: "Proactively greet visitors and help them find products in your Shopify store",
    template: "shopify-product-discovery" as IntegrationTemplateKey,
    badge: "Shopify"
  },
  {
    icon: <IllustrationMissedChatsUnavailable />,
    title: "Shopify Cart Recovery",
    text: "Re-engage visitors who left without buying — AI answers questions to help them complete checkout",
    template: "shopify-cart-recovery" as IntegrationTemplateKey,
    badge: "Shopify"
  },
  {
    icon: <IllustrationChatbotSupportService />,
    title: "BigCommerce Order Status",
    text: "Let AI look up orders, track shipments, and answer account questions from BigCommerce",
    template: "bigcommerce-order-status" as IntegrationTemplateKey,
    badge: "BigCommerce"
  },
  {
    icon: <IllustrationChatbotLeadGen />,
    title: "BigCommerce Product Assistant",
    text: "AI searches your BigCommerce catalog and answers pricing, stock, and product questions",
    template: "bigcommerce-product-assistant" as IntegrationTemplateKey,
    badge: "BigCommerce"
  },
  {
    icon: <IllustrationProactiveChatCommunication />,
    title: "BigCommerce Cart Recovery",
    text: "Re-engage visitors who abandoned their cart — AI uses live store data to remove friction",
    template: "bigcommerce-cart-recovery" as IntegrationTemplateKey,
    badge: "BigCommerce"
  }
];

interface SystemTemplate {
  id: number;
  name: string;
  triggerType: string;
  systemTemplateCategory: string | null;
}

export default function WorkflowsCreate() {
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [siteTemplates, setSiteTemplates] = useState<{ id: number; name: string; triggerType: string }[]>([]);
  const [systemTemplates, setSystemTemplates] = useState<SystemTemplate[]>([]);
  const api = useApi();
  const { track } = useActivityLog();

  useEffect(() => {
    async function loadSiteTemplates() {
      try {
        const resp = await api.messaging.get("Workflows/Templates");
        const data = await resp.json();
        setSiteTemplates(Array.isArray(data) ? data : []);
      } catch {
        // non-fatal — section is simply hidden when empty
      }
    }
    async function loadSystemTemplates() {
      try {
        const resp = await api.messaging.get("Workflows/SystemTemplates");
        const data = await resp.json();
        setSystemTemplates(Array.isArray(data) ? data : []);
      } catch {
        // non-fatal
      }
    }
    loadSiteTemplates();
    loadSystemTemplates();
  }, [api.messaging]);

  function onClickCard(template: string) {
    if (template === "blank") {
      setShowTriggerModal(true);
      return;
    }
    if (template === "build-with-ai") {
      return;
    }
    // General templates (no nodes yet — just open trigger picker)
    return;
  }

  async function onClickSystemTemplate(template: SystemTemplate) {
    setLoading(true);
    try {
      const response = await api.messaging.post(
        `Workflows/SystemTemplates/${template.id}/Instantiate`,
        {}
      );
      const result = await response.json();
      const workflowId = result?.id ?? result;

      track({
        category: "Workflows",
        action: "WorkflowCreatedFromSystemTemplate",
        entityType: "Workflow",
        entityId: String(workflowId),
        detail: { systemTemplateId: template.id, templateName: template.name }
      });

      navigate(`/workflows/${workflowId}`);
    } catch {
      setLoading(false);
    }
  }

  async function onClickSiteTemplate(template: { id: number; name: string }) {
    setLoading(true);
    try {
      const response = await api.messaging.post("Workflows", {
        templateId: template.id,
        name: `${template.name} - Instance`
      });
      const workflowId = await response.json();

      track({
        category: "Workflows",
        action: "WorkflowCreatedFromSiteTemplate",
        entityType: "Workflow",
        entityId: String(workflowId),
        detail: { templateId: template.id, templateName: template.name }
      });

      navigate(`/workflows/${workflowId}`);
    } catch {
      setLoading(false);
    }
  }

  async function onClickIntegrationTemplate(templateKey: IntegrationTemplateKey) {
    const tpl = TEMPLATE_NODES[templateKey];
    setLoading(true);
    const response = await api.messaging.post("Workflows", {
      trigger: tpl.trigger,
      name: tpl.name,
      initialNodesBlob: JSON.stringify(tpl.nodes)
    });
    const workflowId = await response.json();

    track({
      category: "Workflows",
      action: "WorkflowCreatedFromTemplate",
      entityType: "Workflow",
      entityId: String(workflowId),
      detail: { template: templateKey, trigger: tpl.trigger }
    });

    navigate(`/workflows/${workflowId}`);
  }

  async function selectTrigger(triggerId: string) {
    setShowTriggerModal(false);
    setLoading(true);
    const response = await api.messaging.post("Workflows", {
      trigger: triggerId
    });
    const workflowId = await response.json();

    track({
      category: "Workflows",
      action: "WorkflowCreated",
      entityType: "Workflow",
      entityId: String(workflowId),
      detail: { trigger: triggerId }
    });

    navigate(`/workflows/${workflowId}`);
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <CardGrouping
        header="Start Building"
        cards={startBuildingCards}
        onClick={onClickCard}
      />
      <CardGrouping
        header="Use a Template"
        cards={templateCards}
        onClick={onClickCard}
      />
      {systemTemplates.length > 0 && (
        <SystemTemplateGallery
          templates={systemTemplates}
          onInstall={onClickSystemTemplate}
        />
      )}
      {siteTemplates.length > 0 && (
        <div className="mb-8">
          <div className="font-semibold mb-1">Your Templates</div>
          <p className="text-sm text-gray-500 mb-4">
            Create a workflow pre-loaded with a structure you&apos;ve saved as a template. It will stay in sync with the template automatically.
          </p>
          <div className="flex flex-wrap gap-4">
            {siteTemplates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => onClickSiteTemplate(template)}
                aria-label={`Create workflow from template: ${template.name}`}
                className="group w-72 h-40 p-4 flex flex-col border-2 border-violet-200 rounded-lg items-center justify-center gap-3 cursor-pointer hover:border-violet-500 hover:shadow-sm bg-violet-50 text-left"
              >
                <div className="w-full flex justify-end">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                    Template
                  </span>
                </div>
                <span className="font-semibold text-sm text-violet-800 group-hover:text-violet-600 text-center">
                  {template.name}
                </span>
                <span className="text-xs text-violet-500">
                  Click to create a linked instance
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="mb-8">
        <div className="font-semibold mb-1">Integration Templates</div>
        <p className="text-sm text-gray-500 mb-4">
          Pre-built workflows for NetSuite and Shopify — open in the editor to
          connect your AI config and start chatting.
        </p>
        <div className="flex flex-wrap gap-4">
          {integrationTemplateCards.map((card) => (
            <IntegrationCard
              key={card.template}
              icon={card.icon}
              title={card.title}
              text={card.text}
              badge={card.badge}
              onClick={() => onClickIntegrationTemplate(card.template)}
            />
          ))}
        </div>
      </div>
      {showTriggerModal && (
        <TriggerModal
          onSelect={selectTrigger}
          onClose={() => setShowTriggerModal(false)}
        />
      )}
    </>
  );
}

function CardGrouping(props: {
  header: string;
  cards: {
    icon: React.ReactElement;
    title: string;
    text: string;
    template: string;
  }[];
  onClick: (template: string) => void;
}) {
  return (
    <div className="mb-8">
      <div className="font-semibold">{props.header}</div>
      <div className="flex flex-wrap gap-4 my-4">
        {props.cards.map((card, index) => (
          <WorkflowCard
            onClick={() => props.onClick(card.template)}
            key={index}
            {...card}
          />
        ))}
      </div>
    </div>
  );
}

function WorkflowCard(props: {
  icon: React.ReactElement;
  title: string;
  text: string;
  onClick: () => void;
}) {
  return (
    <div
      onClick={props.onClick}
      className="group w-72 h-56 p-4 flex flex-col border rounded-lg items-center justify-between cursor-pointer hover:border-cornflower-blue-500 hover:shadow-sm"
    >
      <div className="w-20 h-20 mt-2">{props.icon}</div>
      <div className="flex flex-col items-center justify-between">
        <span className="font-semibold p-2 group-hover:text-cornflower-blue-500">
          {props.title}
        </span>
        <span className="text-sm text-center text-gray-500">{props.text}</span>
      </div>
    </div>
  );
}

// ── Category display helpers ──────────────────────────────────────────────────
const CATEGORY_ORDER = ["Routing", "Escalation", "Availability", "Lead Capture", "Post-Chat"];
const CATEGORY_COLORS: Record<string, string> = {
  Routing: "bg-blue-100 text-blue-700",
  Escalation: "bg-orange-100 text-orange-700",
  Availability: "bg-emerald-100 text-emerald-700",
  "Lead Capture": "bg-purple-100 text-purple-700",
  "Post-Chat": "bg-pink-100 text-pink-700"
};

function SystemTemplateGallery({
  templates,
  onInstall
}: {
  templates: SystemTemplate[];
  onInstall: (t: SystemTemplate) => void;
}) {
  // Group by category, putting uncategorised at end
  const grouped = new Map<string, SystemTemplate[]>();
  for (const t of templates) {
    const cat = t.systemTemplateCategory || "Other";
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(t);
  }
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => grouped.has(c)),
    ...[...grouped.keys()].filter((c) => !CATEGORY_ORDER.includes(c))
  ];

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-1">
        <div className="font-semibold">Velaro Templates</div>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cornflower-blue-100 text-cornflower-blue-700">
          {templates.length} available
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Pre-built routing and automation templates from Velaro. Install a copy and
        customize it — your version is independent and fully editable.
      </p>
      {orderedCategories.map((category) => (
        <div key={category} className="mb-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {category}
          </div>
          <div className="flex flex-wrap gap-3">
            {(grouped.get(category) ?? []).map((t) => (
              <SystemTemplateCard
                key={t.id}
                template={t}
                onInstall={() => onInstall(t)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SystemTemplateCard({
  template,
  onInstall
}: {
  template: SystemTemplate;
  onInstall: () => void;
}) {
  const cat = template.systemTemplateCategory || "Other";
  const colorClass = CATEGORY_COLORS[cat] ?? "bg-slate-100 text-slate-600";

  return (
    <div className="w-64 border border-slate-200 rounded-lg p-4 flex flex-col gap-2 hover:border-cornflower-blue-400 hover:shadow-sm transition-all bg-white">
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-sm text-slate-800 leading-tight">
          {template.name}
        </span>
        <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${colorClass}`}>
          {cat}
        </span>
      </div>
      {template.triggerType && (
        <div className="text-xs text-slate-400 font-mono">{template.triggerType}</div>
      )}
      <button
        type="button"
        className="mt-auto w-full text-center text-xs font-semibold text-cornflower-blue-600 hover:text-cornflower-blue-800 border border-cornflower-blue-200 hover:border-cornflower-blue-400 rounded-md py-1.5 transition-colors"
        onClick={onInstall}
        aria-label={`Install ${template.name}`}
      >
        Install →
      </button>
    </div>
  );
}

function IntegrationCard(props: {
  icon: React.ReactElement;
  title: string;
  text: string;
  badge: string;
  onClick: () => void;
}) {
  const badgeColor =
    props.badge === "Shopify"
      ? "bg-green-100 text-green-700"
      : props.badge === "BigCommerce"
      ? "bg-slate-800 text-white"
      : "bg-blue-100 text-blue-700";

  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={`Create workflow from template: ${props.title}`}
      className="group w-72 h-56 p-4 flex flex-col border rounded-lg items-center justify-between cursor-pointer hover:border-cornflower-blue-500 hover:shadow-sm text-left"
    >
      <div className="w-full flex justify-end">
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeColor}`}>
          {props.badge}
        </span>
      </div>
      <div className="w-16 h-16">{props.icon}</div>
      <div className="flex flex-col items-center">
        <span className="font-semibold text-sm group-hover:text-cornflower-blue-500">
          {props.title}
        </span>
        <span className="text-xs text-center text-gray-500 mt-1">{props.text}</span>
      </div>
    </button>
  );
}
